import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  flattenGroups,
  groupDepth,
  MAX_GROUP_DEPTH,
  subtreeHeight,
  TvControllerGroup,
} from '@/lib/schema/tvControllerGroup';
import { useEffect, useMemo, useState } from 'react';

const TOP_LEVEL = '__top__';

type MoveGroupDialogProps = {
  group: TvControllerGroup | null;
  groups: TvControllerGroup[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    group: TvControllerGroup,
    parentId: string | null
  ) => Promise<void>;
};

export function MoveGroupDialog({
  group,
  groups,
  onOpenChange,
  onSubmit,
}: MoveGroupDialogProps) {
  const [target, setTarget] = useState(TOP_LEVEL);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (group) setTarget(TOP_LEVEL);
  }, [group]);

  // A group can't move into itself or its own subgroups, nor anywhere that
  // would push its deepest subgroup past the depth limit.
  const targets = useMemo(() => {
    if (!group) return [];
    const own = new Set(flattenGroups([group]).map((g) => g.id));
    const height = subtreeHeight(group);
    return flattenGroups(groups)
      .filter((g) => !own.has(g.id))
      .map((g) => ({ group: g, depth: groupDepth(groups, g.id) }))
      .filter(({ depth }) => depth + height <= MAX_GROUP_DEPTH);
  }, [group, groups]);

  return (
    <Dialog open={!!group} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Move "{group?.name}"</DialogTitle>
          <DialogDescription>
            Its subgroups and TVs move with it.
          </DialogDescription>
        </DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TOP_LEVEL}>Top level</SelectItem>
            {targets.map(({ group: g, depth }) => (
              <SelectItem key={g.id} value={g.id}>
                {'  '.repeat(depth - 1)}
                {g.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSaving}
            onClick={async () => {
              if (!group) return;
              setIsSaving(true);
              try {
                await onSubmit(group, target === TOP_LEVEL ? null : target);
                onOpenChange(false);
              } catch {
                // The page already toasted the failure; keep the dialog open.
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? 'Moving...' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
